import React, { useState, useMemo } from 'react';
import { Car, Plus, Edit, Trash2, Search, TrendingUp, Megaphone, CheckCircle2, Circle } from 'lucide-react';
import { useData } from '../context/DataContext';
import HandelAutoEditor from '../components/HandelAutoEditor';
import VerkoopAdvertentie from '../components/VerkoopAdvertentie';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const sumKosten = (a) => Array.isArray(a.kostenposten)
  ? a.kostenposten.reduce((s, k) => s + (Number(k.kosten) || 0), 0)
  : (Number(a.totaalKosten) || 0);
const calcMarge = (a) => (Number(a.verkoopPrijs) || 0) - (Number(a.inkoopPrijs) || 0) - sumKosten(a);
const calcKostprijs = (a) => (Number(a.inkoopPrijs) || 0) + sumKosten(a);
const calcRoi = (a) => { const k = calcKostprijs(a); return k > 0 ? Math.round((calcMarge(a) / k) * 100) : 0; };
const dagenInVoorraad = (a) => {
  if (!a.ingekochtOp) return null;
  const start = new Date(a.ingekochtOp);
  const eind = a.status === 'Verkocht' && a.verkochtOp ? new Date(a.verkochtOp) : new Date();
  const d = Math.floor((eind - start) / 86400000);
  return d >= 0 ? d : null;
};

export default function AutoHandel() {
  const { t } = useT();
  const { handelAutos: list, deleteHandelAuto } = useData();
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState('Alle');
  const [editing, setEditing] = useState(null); // null | {} new | {existing}
  const [adAuto, setAdAuto] = useState(null); // handelauto voor verkoopadvertentie

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return list.filter(a => {
      const matchF = !f || a.kenteken.toLowerCase().includes(f) || a.merk.toLowerCase().includes(f) || a.model.toLowerCase().includes(f);
      const matchS = status === 'Alle' || a.status === status;
      return matchF && matchS;
    });
  }, [list, filter, status]);

  const selected = list.find(a => a.id === selectedId) || list[0];
  const totaalVoorraad = list.filter(a => a.status === 'Voorraad').length;
  const totaalInkoop = list.filter(a => a.status === 'Voorraad').reduce((s, a) => s + a.inkoopPrijs, 0);
  const verwachteMarge = list.filter(a => a.status === 'Voorraad').reduce((s, a) => s + calcMarge(a), 0);

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.cartrade.title')}</h2><p>{t('page.cartrade.sub')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button data-testid="btn-handel-add" className="cs-btn primary" onClick={() => setEditing({})}><Plus size={14} /> {t('Auto toevoegen')}</button>
          <button data-testid="btn-handel-advertentie" className="cs-btn" disabled={!selected} onClick={() => selected && setAdAuto(selected)} title={t('Maak verkoopadvertentie', 'Maak verkoopadvertentie')}><Megaphone size={14} /> {t('Verkoopadvertentie', 'Verkoopadvertentie')}</button>
          <button className="cs-btn" disabled={!selected} onClick={() => setEditing({ ...selected })}><Edit size={14} /> {t('Bewerken')}</button>
          <button className="cs-btn danger" onClick={async () => { if (!selected) return; if (!(await confirmDialog(t('Auto verwijderen?')))) return; try { await deleteHandelAuto(selected.id); toast.success(t('Auto verwijderd')); setSelectedId(null); } catch { toast.error(t('Mislukt')); } }}><Trash2 size={14} /> {t('Verwijderen')}</button>
        </div>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi"><div className="label">{t('In voorraad')}</div><div className="value">{totaalVoorraad}</div></div>
        <div className="cs-kpi"><div className="label">{t('Totaal inkoopwaarde')}</div><div className="value">{eur(totaalInkoop)}</div></div>
        <div className="cs-kpi"><div className="label">{t('Verwachte marge')}</div><div className="value" style={{ color: 'var(--cs-green)' }}>{eur(verwachteMarge)}</div></div>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t("Zoek kenteken, merk of model...","Zoek kenteken, merk of model...")} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 2 }}>
          {['Alle', 'Voorraad', 'Reservering', 'Verkocht', 'Overschreven', 'Geschorst'].map(s => (
            <button key={s} className="cs-btn ghost" style={{ padding: '4px 12px', background: status === s ? 'var(--cs-accent-soft)' : 'transparent', color: status === s ? 'var(--cs-accent)' : 'inherit' }} onClick={() => setStatus(s)}>{t(s,s)}</button>
          ))}
        </div>
      </div>

      <div className="cs-master-detail">
        <div className="cs-card">
          <table className="cs-table">
            <thead><tr><th>{t('Kenteken')}</th><th>{t('Merk / Model')}</th><th>{t('Bj')}</th><th>{t('KM')}</th><th>{t('Inkoop')}</th><th>{t('Verkoop')}</th><th>{t('Marge')}</th><th>{t('Status')}</th></tr></thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className={a.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(a.id)} onDoubleClick={() => setEditing({ ...a })} style={{ cursor: 'pointer' }} title={("Dubbelklik om te bewerken","Dubbelklik om te bewerken")}>
                  <td data-label={t('Kenteken')} style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{a.kenteken}</td>
                  <td data-label={t('Merk / Model')}>{a.merk} {a.model}</td>
                  <td data-label={t('Bj')}>{a.bouwjaar}</td>
                  <td data-label={t('KM')}>{a.kmStand.toLocaleString('nl-NL')}</td>
                  <td data-label={t('Inkoop')}>{eur(a.inkoopPrijs)}</td>
                  <td data-label={t('Verkoop')}>{eur(a.verkoopPrijs)}</td>
                  <td data-label={t('Marge')} style={{ color: 'var(--cs-green)', fontWeight: 600 }}>{eur(calcMarge(a))}</td>
                  <td data-label={t('Status')}><span className={`cs-badge ${a.status === 'Voorraad' ? 'blue' : a.status === 'Reservering' ? 'amber' : (a.status === 'Geschorst' ? 'red' : (a.status === 'Overschreven' ? 'gray' : 'green'))}`}>{t(a.status, a.status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="cs-empty">{t('Geen voertuigen gevonden.', 'Geen voertuigen gevonden.')}</div>}
        </div>

        <div className="cs-card padded">
          {selected ? (
            <>
              <h3 className="cs-card-title"><Car size={16} /> {selected.merk} {selected.model}</h3>
              <div className="cs-detail-row"><span className="lbl">{t('Kenteken','Kenteken')}</span><span className="val" style={{ fontFamily: 'ui-monospace, monospace' }}>{selected.kenteken}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Bouwjaar','Bouwjaar')}</span><span className="val">{selected.bouwjaar}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('KM stand','KM stand')}</span><span className="val">{selected.kmStand.toLocaleString('nl-NL')}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Inkoopprijs','Inkoopprijs')}</span><span className="val">{eur(selected.inkoopPrijs)}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Verkoopprijs','Verkoopprijs')}</span><span className="val" style={{ fontWeight: 700 }}>{eur(selected.verkoopPrijs)}</span></div>
              {sumKosten(selected) > 0 && (
                <div className="cs-detail-row"><span className="lbl">{t('Kosten / vervangen','Kosten / vervangen')} </span><span className="val" style={{ color: 'var(--cs-amber, #b45309)' }}>−{eur(sumKosten(selected))}</span></div>
              )}
              <div className="cs-detail-row"><span className="lbl">{t('Marge')}</span><span className="val" style={{ color: calcMarge(selected) >= 0 ? 'var(--cs-green)' : 'var(--cs-red)', fontWeight: 600 }}><TrendingUp size={12} style={{ verticalAlign: 'middle' }} /> {eur(calcMarge(selected))}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Status')}</span><span className="val">{t(selected.status,selected.status)}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Ingekocht op','Ingekocht op')}</span><span className="val">{new Date(selected.ingekochtOp).toLocaleDateString('nl-NL')}</span></div>
              <div className="cs-detail-row"><span className="lbl">{t('Notitie')}</span><span className="val">{selected.notitie || '-'}</span></div>

              {/* Flip-calculatie */}
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'var(--cs-card-alt)' }} data-testid="flip-calc">
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cs-text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>{t('Flip-calculatie', 'Flip-calculatie')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <FlipKpi label={t('Kostprijs', 'Kostprijs')} value={eur(calcKostprijs(selected))} hint={t('inkoop + kosten', 'inkoop + kosten')} />
                  <FlipKpi label={t('Netto marge', 'Netto marge')} value={eur(calcMarge(selected))} color={calcMarge(selected) >= 0 ? 'var(--cs-green)' : 'var(--cs-red)'} testid="flip-marge" />
                  <FlipKpi label="ROI" value={`${calcRoi(selected)}%`} color={calcRoi(selected) >= 0 ? 'var(--cs-green)' : 'var(--cs-red)'} hint={t('marge ÷ kostprijs', 'marge ÷ kostprijs')} testid="flip-roi" />
                  <FlipKpi label={selected.status === 'Verkocht' ? t('Verkooptijd', 'Verkooptijd') : t('Dagen in voorraad', 'Dagen in voorraad')} value={dagenInVoorraad(selected) != null ? `${dagenInVoorraad(selected)} ${t('dgn', 'dgn')}` : '—'} color={selected.status !== 'Verkocht' && dagenInVoorraad(selected) > 90 ? 'var(--cs-red)' : 'inherit'} testid="flip-dagen" />
                </div>
              </div>

              {/* RDW-titelchecklist */}
              {Array.isArray(selected.titelChecklist) && selected.titelChecklist.length > 0 && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid var(--cs-border)' }} data-testid="titel-checklist-detail">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cs-text-muted)', textTransform: 'uppercase' }}>{t('RDW-titelchecklist', 'RDW-titelchecklist')}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: selected.titelChecklist.every(c => c.done) ? 'var(--cs-green)' : 'var(--cs-amber, #b45309)' }}>
                      {selected.titelChecklist.filter(c => c.done).length}/{selected.titelChecklist.length}
                    </span>
                  </div>
                  {selected.titelChecklist.map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, padding: '3px 0' }}>
                      {c.done ? <CheckCircle2 size={14} style={{ color: 'var(--cs-green)' }} /> : <Circle size={14} style={{ color: 'var(--cs-text-muted)' }} />}
                      <span style={{ color: c.done ? 'var(--cs-text)' : 'var(--cs-text-muted)' }}>{t(c.label, c.label)}</span>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(selected.kostenposten) && selected.kostenposten.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--cs-border)' }} data-testid="kostenposten-detail">
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cs-text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Vervangen / kosten</div>
                  {selected.kostenposten.map(k => (
                    <div key={k.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '4px 0', gap: 8 }}>
                      <span style={{ flex: 1 }}>
                        { k.omschrijving || t('(geen omschrijving)', '(geen omschrijving)') }
                        {k.voorgeschotenDoor && <span style={{ color: 'var(--cs-text-muted)' }}> · {t('voorgeschoten door','voorgeschoten door')} {k.voorgeschotenDoor}</span>}
                      </span>
                      <b style={{ whiteSpace: 'nowrap' }}>{eur(Number(k.kosten) || 0)}</b>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : <div className="cs-empty">{t('Selecteer een voertuig.', 'Selecteer een voertuig.')}</div>}
        </div>
      </div>
      {editing && <HandelAutoEditor auto={editing.id ? editing : null} onClose={() => setEditing(null)} />}
      {adAuto && <VerkoopAdvertentie auto={adAuto} onClose={() => setAdAuto(null)} />}
    </div>
  );
}

const FlipKpi = ({ label, value, color, hint, testid }) => (
  <div data-testid={testid} style={{ background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: '7px 9px' }}>
    <div style={{ fontSize: 10.5, color: 'var(--cs-text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 800, color: color || 'var(--cs-text)' }}>{value}</div>
    {hint && <div style={{ fontSize: 10, color: 'var(--cs-text-muted)' }}>{hint}</div>}
  </div>
);
